import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Search, Download, UserCheck, Filter } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "expiring", label: "Expiring ≤15d" },
  { key: "expired", label: "Expired" },
  { key: "never", label: "Never paid" },
];

const BADGE = { active: "bg-green-100 text-green-700", expired: "bg-red-100 text-red-700", none: "bg-gray-100 text-gray-600" };

export default function AdminSubscriptions() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [granting, setGranting] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    const { data } = await api.get("/admin/subscriptions", { params: { q, filter } });
    setRows(data.subscriptions || []);
  }, [q, filter]);

  useEffect(() => { load(); }, [load]);

  const grant = async (r) => {
    const reason = window.prompt(`Manually activate/extend 365 days for ${r.name} (${r.mobile}).\nEnter a reason (e.g. cash payment):`, "Offline / cash payment");
    if (reason === null) return;
    setGranting(r.id);
    try {
      await api.post(`/admin/subscriptions/${r.id}/grant`, { days: 365, reason });
      toast.success("Plan activated for 365 days.");
      load();
    } catch { toast.error("Could not activate plan."); }
    finally { setGranting(null); }
  };

  const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Subscriptions</h1>
        <a href={`${API}/admin/subscriptions/export`} className="btn-ghost text-sm" data-testid="sub-export"><Download className="w-4 h-4" /> Export Excel</a>
      </div>

      <div className="card-thali space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-thali-ink/40" />
          <input className="input-thali pl-9" placeholder="Search by name or mobile" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sub-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`sub-filter-${f.key}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${filter === f.key ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30 text-thali-ink/70"}`}>
              <Filter className="w-3 h-3 inline mr-1" />{f.label}
            </button>
          ))}
        </div>
      </div>

      {rows === null ? <Loader /> : (
        <div className="card-thali overflow-x-auto p-0" data-testid="sub-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="bg-thali-cream text-thali-ink/70">
              <th className="text-left p-2 pl-4">Name</th><th className="p-2">Mobile</th><th className="p-2">Status</th>
              <th className="p-2">Start</th><th className="p-2">Expiry</th><th className="p-2">Days</th><th className="p-2 pr-4">Action</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-thali-turmeric/10" data-testid={`sub-row-${r.id}`}>
                  <td className="p-2 pl-4"><div className="font-medium">{r.name}</div><div className="text-xs text-thali-ink/50">{r.school}</div></td>
                  <td className="p-2 text-center">{r.mobile}</td>
                  <td className="p-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-bold ${BADGE[r.status] || "bg-gray-100"}`}>{r.status}</span></td>
                  <td className="p-2 text-center">{fmt(r.start)}</td>
                  <td className="p-2 text-center">{fmt(r.expiry)}</td>
                  <td className="p-2 text-center font-num font-bold">{r.is_active ? r.days_left : "—"}</td>
                  <td className="p-2 pr-4 text-center">
                    <button onClick={() => grant(r)} disabled={granting === r.id} className="btn-ghost text-xs px-2 py-1" data-testid={`sub-grant-${r.id}`}>
                      <UserCheck className="w-3.5 h-3.5" /> {granting === r.id ? "…" : (r.is_active ? "Extend" : "Activate")}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-thali-ink/50">No matching users.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
